import React, { useState, useEffect } from 'react';
import { Asset } from '../types';
import { getAssets, getAssetFolders, createAsset, updateAsset, deleteAsset } from '../services/marketingApi';
import { Empty, Modal, PageFrame, Pill, Section, Tabs, Th, Thumb, btn, btnTone, input, label, td, trClick } from '../components/Kit';

/** Assets — the marketing file store as one list: folders first, then each
 *  asset with its picture, type, tags and last change. A row opens its editor. */

type AssetType = Asset['type'];
const TYPE_LABEL: Record<AssetType, string> = { text: 'Text', image: 'Image', pdf: 'PDF', video: 'Video' };
const when = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

const FolderGlyph = () => (
  <span className="w-16 h-11 rounded-md bg-sage/25 flex-none grid place-items-center text-forest">
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  </span>
);

/** A thumbnail for any asset type: the image itself, or a quiet type tile. */
const AssetThumb: React.FC<{ a: Asset }> = ({ a }) =>
  a.type === 'image' && a.url ? <Thumb src={a.url} /> : (
    <span className="w-16 h-11 rounded-md bg-charcoal/[0.06] flex-none grid place-items-center text-[10.5px] font-semibold uppercase tracking-[0.06em] text-graphite">
      {a.type === 'video' ? '▶' : TYPE_LABEL[a.type]}
    </span>
  );

/** A file picker you can also drop onto. */
const DropZone: React.FC<{ accept: string; file: File | null; onFile: (f: File | null) => void; hint: string }> = ({ accept, file, onFile, hint }) => {
  const [over, setOver] = useState(false);
  return (
    <label
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onFile(e.dataTransfer.files?.[0] || null); }}
      className={`flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed px-4 py-6 text-center cursor-pointer transition-colors ${
        over ? 'border-forest bg-sage/20' : 'border-charcoal/20 hover:border-forest hover:bg-charcoal/[0.02]'}`}>
      <input type="file" accept={accept} className="sr-only" onChange={(e) => onFile(e.target.files?.[0] || null)} />
      {file
        ? <><span className="text-[13.5px] font-semibold text-charcoal truncate max-w-full">{file.name}</span>
            <span className="text-[12px] text-graphite">{(file.size / 1024 / 1024).toFixed(1)} MB · click or drop to replace</span></>
        : <><span className="text-[13.5px] font-semibold text-charcoal">Drop a file or click to choose</span>
            <span className="text-[12px] text-graphite">{hint}</span></>}
    </label>
  );
};

const Assets: React.FC = () => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [filterType, setFilterType] = useState<string>('');
  const [filterTags, setFilterTags] = useState<string>('');
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [subfolders, setSubfolders] = useState<string[]>([]);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [formFolder, setFormFolder] = useState('');
  const [allFolders, setAllFolders] = useState<string[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  // Form state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState<AssetType>('text');
  const [formContent, setFormContent] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [formTags, setFormTags] = useState('');
  const [formFile, setFormFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fetchAssets = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await getAssets(filterTags || undefined, filterType || undefined, currentFolder);
      setAssets(response.assets);
      setSubfolders(response.subfolders || []);
    } catch (error: any) {
      console.error('Error fetching assets:', error);
      setLoadError(error?.response?.data?.detail || error?.message || 'unknown error');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, filterTags, currentFolder]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSaveError(null);

    try {
      const formData = new FormData();
      formData.append('name', formName);
      formData.append('type', formType);
      formData.append('tags', formTags);

      if (formContent) {
        formData.append('content', formContent);
      }
      if (formType === 'video') {
        formData.append('url', formUrl);
      } else if (formFile) {
        formData.append('file', formFile);
      }

      if (formFolder) {
        formData.append('folder', formFolder);
      }

      if (editingId) {
        await updateAsset(editingId, formData);
      } else {
        await createAsset(formData);
      }
      setShowModal(false);
      resetForm();
      fetchAssets();
    } catch (error: any) {
      console.error('Error saving asset:', error);
      setSaveError(error?.response?.data?.detail || error?.message || 'Failed to save asset');
    }
    setSubmitting(false);
  };

  const fetchFolders = async () => {
    try {
      const res = await getAssetFolders();
      setAllFolders(res.folders || []);
    } catch (e) {
      console.error('Error fetching folders:', e);
    }
  };

  const openEdit = (asset: Asset) => {
    setEditingId(asset.id);
    setFormName(asset.name);
    setFormType(asset.type);
    setFormContent(asset.content || '');
    setFormUrl(asset.url || '');
    setFormTags(asset.tags.join(', '));
    setFormFile(null);
    setFormFolder(asset.folder || '');
    setSaveError(null);
    fetchFolders();
    setShowModal(true);
  };

  const openCreate = () => {
    resetForm();
    setFormFolder(currentFolder || '');
    fetchFolders();
    setShowModal(true);
  };

  const closeModal = () => { if (!submitting) { setShowModal(false); resetForm(); } };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this asset?')) return;
    try {
      await deleteAsset(id);
      fetchAssets();
    } catch (error) {
      console.error('Error deleting asset:', error);
    }
  };

  const copyUrl = async (asset: Asset) => {
    if (!asset.url) return;
    await navigator.clipboard.writeText(asset.url);
    setCopied(asset.id);
    setTimeout(() => setCopied((c) => (c === asset.id ? null : c)), 1500);
  };

  const resetForm = () => {
    setEditingId(null);
    setFormName('');
    setFormType('text');
    setFormContent('');
    setFormUrl('');
    setFormTags('');
    setFormFile(null);
    setFormFolder('');
    setSaveError(null);
  };

  const enter = (name: string) => setCurrentFolder(currentFolder ? `${currentFolder}/${name}` : name);

  const crumbs = (
    <nav className="flex items-center gap-1.5 text-[13.5px] min-w-0" aria-label="Folder">
      <button type="button" onClick={() => setCurrentFolder(null)}
              className={`font-semibold hover:text-forest ${currentFolder ? 'text-graphite' : 'text-charcoal'}`}>All assets</button>
      {currentFolder && currentFolder.split('/').map((segment, i, arr) => {
        const path = arr.slice(0, i + 1).join('/');
        const isLast = i === arr.length - 1;
        return (
          <React.Fragment key={path}>
            <span className="text-charcoal/30" aria-hidden>›</span>
            <button type="button" onClick={() => setCurrentFolder(path)}
                    className={`font-semibold hover:text-forest truncate max-w-[200px] ${isLast ? 'text-charcoal' : 'text-graphite'}`}>{segment}</button>
          </React.Fragment>
        );
      })}
    </nav>
  );

  const bar = (
    <>
      {crumbs}
      <span className="flex-1" />
      <input type="search" placeholder="Filter by tags" value={filterTags}
             onChange={(e) => setFilterTags(e.target.value)} className={`${input} h-8 w-[220px]`} />
      <Tabs value={filterType} onChange={setFilterType}
            items={[['', 'All'], ['image', 'Images'], ['pdf', 'PDFs'], ['video', 'Videos'], ['text', 'Text']]} />
    </>
  );

  const action = showNewFolderInput ? (
    <form onSubmit={(e) => {
      e.preventDefault();
      if (newFolderName.trim()) {
        enter(newFolderName.trim());
        setNewFolderName('');
        setShowNewFolderInput(false);
      }
    }} className="flex gap-2">
      <input type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
             placeholder="Folder name" autoFocus className={`${input} h-8`} />
      <button type="submit" className={`${btn} ${btnTone.forest}`}>Create</button>
      <button type="button" onClick={() => { setShowNewFolderInput(false); setNewFolderName(''); }} className={`${btn} ${btnTone.ghost}`}>Cancel</button>
    </form>
  ) : (
    <>
      <button type="button" onClick={() => setShowNewFolderInput(true)} className={`${btn} ${btnTone.charcoal}`}>New folder</button>
      <button type="button" onClick={openCreate} className={`${btn} ${btnTone.forest}`}>+ New asset</button>
    </>
  );

  const count = assets.length + subfolders.length;
  const rowBtn = `${btn} ${btnTone.ghost} h-7 px-2 text-[12px]`;

  return (
    <PageFrame title="Assets" subline="Marketing files, captions and videos" action={action} bar={bar}>
      <Section title={loading ? 'Loading…' : `${assets.length} asset${assets.length === 1 ? '' : 's'}${subfolders.length ? ` · ${subfolders.length} folder${subfolders.length === 1 ? '' : 's'}` : ''}`}>
        {loadError && <div className="px-3 py-3 text-[13px] text-brick">Couldn't load assets: {loadError}</div>}
        <table className="w-full min-w-[820px] border-collapse">
          <thead><tr>
            <Th left>Name</Th><Th left>Type</Th><Th left>Tags</Th><Th>Updated</Th>
            <th className="w-[1%] border-b border-charcoal/10" />
          </tr></thead>
          <tbody>
            {!loading && !loadError && count === 0 && <Empty cols={5}>No assets here{filterType || filterTags ? ' match these filters' : ''}.</Empty>}
            {subfolders.map((folderName) => (
              <tr key={`folder-${folderName}`} onClick={() => enter(folderName)} className={`${trClick()} group`}>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-3.5">
                    <FolderGlyph />
                    <span className="font-semibold text-[14px] text-charcoal group-hover:text-forest">{folderName}</span>
                  </div>
                </td>
                <td className={`${td} text-graphite text-[13px]`}>Folder</td>
                <td /><td />
                <td className="pr-4 text-right text-charcoal/25 group-hover:text-forest transition-colors" aria-hidden>›</td>
              </tr>
            ))}
            {assets.map((asset) => (
              <tr key={asset.id} onClick={() => openEdit(asset)} className={`${trClick()} group`}>
                <td className="px-3 py-2.5 max-w-[460px]">
                  <div className="flex items-center gap-3.5 min-w-0">
                    <AssetThumb a={asset} />
                    <div className="min-w-0">
                      <div className="font-semibold text-[14px] text-charcoal group-hover:text-forest truncate">{asset.name}</div>
                      {(asset.content || (asset.type === 'video' && asset.url)) && (
                        <div className="text-[12px] text-graphite truncate mt-0.5">{asset.content || asset.url}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className={`${td} text-[13px] text-charcoal`}>{TYPE_LABEL[asset.type]}</td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-1 max-w-[280px]">
                    {asset.tags.map((tag, i) => <Pill key={i} tone="ok">{tag}</Pill>)}
                  </div>
                </td>
                <td className={`${td} text-right text-[13px] text-graphite`}>{when(asset.updated_at)}</td>
                <td className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    {asset.url && <a href={asset.url} target="_blank" rel="noopener noreferrer" className={rowBtn}>Open</a>}
                    {asset.url && <button type="button" onClick={() => copyUrl(asset)} className={rowBtn}>{copied === asset.id ? 'Copied' : 'Copy URL'}</button>}
                    <button type="button" onClick={() => openEdit(asset)} className={rowBtn}>Edit</button>
                    <button type="button" onClick={() => handleDelete(asset.id)} className={`${rowBtn} !text-brick hover:!bg-brick/5`}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {showModal && (
        <Modal
          title={editingId ? 'Edit asset' : 'New asset'}
          onClose={closeModal}
          footer={
            <>
              {saveError && <span className="mr-auto text-[13px] text-brick truncate">{saveError}</span>}
              <button type="button" onClick={closeModal} disabled={submitting} className={`${btn} ${btnTone.ghost}`}>Cancel</button>
              <button type="submit" form="asset-form" disabled={submitting} className={`${btn} ${btnTone.forest}`}>
                {submitting && <span className="w-3.5 h-3.5 rounded-full border-2 border-warm-white/40 border-t-warm-white animate-spin" />}
                {submitting ? (formFile ? 'Uploading…' : 'Saving…') : (editingId ? 'Save' : 'Create')}
              </button>
            </>
          }
        >
          <form id="asset-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-[1fr_auto] gap-4 items-end">
              <div>
                <label className={label}>Name</label>
                <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} required className={`${input} w-full`} />
              </div>
              <div>
                <label className={label}>Type</label>
                <Tabs value={formType} onChange={setFormType}
                      items={[['text', 'Text'], ['image', 'Image'], ['pdf', 'PDF'], ['video', 'Video']]} />
              </div>
            </div>

            {formType === 'video' && (
              <div>
                <label className={label}>YouTube URL</label>
                <input type="url" value={formUrl} onChange={(e) => setFormUrl(e.target.value)}
                       placeholder="https://youtube.com/..." className={`${input} w-full`} />
              </div>
            )}

            {(formType === 'image' || formType === 'pdf') && (
              <div>
                <label className={label}>File</label>
                {editingId && formUrl && !formFile && formType === 'image' && (
                  <img src={formUrl} alt="" className="mb-2 max-h-48 rounded-md bg-charcoal/[0.04]" />
                )}
                <DropZone accept={formType === 'image' ? 'image/*' : '.pdf'} file={formFile} onFile={setFormFile}
                          hint={editingId ? 'Leave empty to keep the current file' : formType === 'image' ? 'Any image' : 'PDF'} />
              </div>
            )}

            <div>
              <label className={label}>Text / caption <span className="normal-case tracking-normal font-normal">(optional)</span></label>
              <textarea value={formContent} onChange={(e) => setFormContent(e.target.value)} rows={3}
                        placeholder="Text that goes with this asset" className={`${input} w-full h-auto py-2`} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={label}>Tags</label>
                <input type="text" value={formTags} onChange={(e) => setFormTags(e.target.value)}
                       placeholder="promo, sale, summer" className={`${input} w-full`} />
              </div>
              <div>
                <label className={label}>Folder</label>
                <select value={formFolder} onChange={(e) => setFormFolder(e.target.value)} className={`${input} w-full`}>
                  <option value="">/ (root)</option>
                  {Array.from(new Set([...allFolders, ...(currentFolder ? [currentFolder] : [])])).sort().map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
            </div>
          </form>
        </Modal>
      )}
    </PageFrame>
  );
};

export default Assets;
